import { Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router'
import type { JSX } from 'react'

import { AuthPanel } from '@/components/auth/auth-panel'
import { VerificationHeader } from '@/components/layout/verification-header'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { LOGO } from '@/lib/assets'
import { useAuthStore } from '@/lib/auth/store'
import { getVerificationState } from '@/lib/auth/verification'

// beforeLoad es intencionalmente síncrono — solo verifica estado en memoria,
// sin llamadas de red. Esto hace que las rutas públicas rendericen de inmediato.
//
// El silent refresh (cookie → access token) ocurre en requireAuth de /_app,
// que es donde importa: el usuario accede a una ruta protegida.
// Si alguien con sesión aterriza en /login, verá el formulario un instante
// hasta que el router resuelva su ruta protegida y lo redirija al dashboard.

const VERIFICATION_ROUTES = new Set(['/verify-email', '/verify-phone'])

export const Route = createFileRoute('/_public')({
  beforeLoad: ({ location }) => {
    const isVerificationRoute = VERIFICATION_ROUTES.has(location.pathname)
    const { token, step } = getVerificationState()

    if (isVerificationRoute) {
      if (token) {
        // Redirigir al paso correcto si la URL no coincide con el step pendiente
        if (location.pathname === '/verify-email' && step === 'phone') {
          throw redirect({ to: '/verify-phone' })
        }
        if (location.pathname === '/verify-phone' && step === 'email') {
          throw redirect({ to: '/verify-email' })
        }
        return
      }
    } else {
      // En rutas no-verificación: si hay proceso de verificación pendiente → redirigir
      if (token) {
        throw redirect({ to: step === 'phone' ? '/verify-phone' : '/verify-email' })
      }
    }

    // Sesión activa en memoria → ir al dashboard (sin red)
    if (useAuthStore.getState().accessToken) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: PublicLayout,
})

function PublicLayout(): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isVerificationRoute = VERIFICATION_ROUTES.has(pathname)

  if (isVerificationRoute) {
    return (
      <div className="flex min-h-[100dvh] flex-col">
        <VerificationHeader />
        <div className="flex flex-1 items-center justify-center py-6 sm:py-10">
          <div className="w-full max-w-sm px-4 sm:px-6">
            <Outlet />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-[100dvh]">
      {/* ── Panel izquierdo: tema inverso — ver auth-panel.tsx ── */}
      <AuthPanel />

      {/* ── Panel derecho: formulario ── */}
      <div className="relative flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-8 lg:px-16">
        {/* Barra superior derecha: toggle de tema */}
        <div className="absolute top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))]">
          <ThemeToggle />
        </div>

        {/* Logo de Abakoo: visible solo en mobile (el panel izquierdo está oculto) */}
        <div className="mb-8 lg:hidden">
          <img src={LOGO.light} alt="Abakoo" className="h-9 w-auto dark:hidden" />
          <img src={LOGO.dark} alt="Abakoo" className="hidden h-9 w-auto dark:block" />
        </div>

        <div className="w-full max-w-md">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
